import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { flutterBridge } from '../utils/flutterBridge';
import { refreshToken as refreshTokenApi } from '../lib/api/user/userApi';
import { subscribeToTokenRefreshState, subscribeToSessionExpired } from '../lib/methods/callApi';
import { devError, devLog, logoutLog } from '../utils/devLogger';
import SessionExpiredModal from '../components/SessionExpiredModal';

interface User {
  userId: string;
  index?: number; // 서버 응답의 index 값 추가
  email: string;
  name?: string;
  providerType?: string | null;
  needMoreInfo: boolean;
  locationType?: string; // 사용자 위치 타입 추가
}

interface UserAuthContextType {
  user: User | null;
  accessToken: string | null;
  isLoggedIn: boolean;
  isInitialized: boolean;
  isAutoLoginInProgress: boolean; // 자동 로그인 진행 중 플래그 추가
  isTokenRefreshing: boolean; // 토큰 갱신 진행 중 플래그 추가
  login: (serverResponseData: any, token: string) => void;
  logout: () => void;
  attemptAutoLogin: (refreshToken: string) => Promise<boolean>;
  setTokenRefreshing: (isRefreshing: boolean) => void; // 토큰 갱신 상태 설정 함수
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

export const UserAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isAutoLoginInProgress, setIsAutoLoginInProgress] = useState<boolean>(false);
  const [isTokenRefreshing, setIsTokenRefreshing] = useState<boolean>(false); // 토큰 갱신 상태 추가
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false); // 세션 만료 모달 상태

  // 토큰 갱신 상태 설정 함수
  const setTokenRefreshing = (isRefreshing: boolean) => {
    setIsTokenRefreshing(isRefreshing);
  };

  // 초기화 시 localStorage에서 토큰 및 사용자 정보 복원
  useEffect(() => {
    const savedToken = localStorage.getItem('accessToken');
    const savedUser = localStorage.getItem('user');
    
    devLog('UserAuthContext 초기화:', { savedToken, savedUser });
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setAccessToken(savedToken);
        setUser(userData);
        devLog('사용자 정보 복원 완료:', { userData, savedToken });
      } catch (error) {
        devError('Failed to parse saved user data:', error);
        // 잘못된 데이터가 있으면 제거
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      }
    } else {
      devLog('저장된 사용자 정보 없음');
    }

    // 전역 토큰 갱신 상태 구독
    const unsubscribe = subscribeToTokenRefreshState((isRefreshing) => {
      setIsTokenRefreshing(isRefreshing);
    });

    // 세션 만료 상태 구독 (API 401 및 소켓 인증 에러 통합 처리)
    const unsubscribeSession = subscribeToSessionExpired((isExpired) => {
      if (isExpired) {
        devLog('🚫 [UserAuthContext] 세션 만료 감지 -> 모달 표시');
        setIsSessionExpired(true);
      }
    });
    
    // Flutter 자동 로그인 이벤트 리스너 등록 (컴포넌트 마운트 시에만)
    const handleFlutterAutoLogin = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { refreshToken, accessToken, userInfo } = customEvent.detail;
      const currentToken = localStorage.getItem('accessToken');
      const currentUser = localStorage.getItem('user');
      
      devLog('🔄 Flutter 자동 로그인 이벤트 수신:', {
        hasRefreshToken: !!refreshToken,
        refreshTokenLength: refreshToken?.length || 0,
        refreshTokenPreview: refreshToken ? refreshToken.substring(0, 30) + '...' : '없음',
        hasAccessToken: !!accessToken,
        hasCurrentToken: !!currentToken,
        hasCurrentUser: !!currentUser
      });
      
      if (!currentToken && !currentUser && refreshToken) {
        // 토큰 유효성 검사
        if (!refreshToken || refreshToken.length < 10) {
          devLog('⚠️ 유효하지 않은 리프레시 토큰:', {
            token: refreshToken,
            length: refreshToken?.length || 0
          });
          return;
        }
        
        setIsAutoLoginInProgress(true); // 자동 로그인 시작
        
        // Flutter에서 accessToken과 userInfo를 함께 전달받았다면 API 호출 없이 바로 처리
        if (accessToken && userInfo) {
          devLog('🎯 Flutter에서 받은 토큰과 사용자 정보로 직접 로그인 처리');
          
          // userInfo에 locationType이 있으면 포함하여 저장
          const userDataWithLocation = {
            ...userInfo,
            locationType: userInfo.locationType // locationType 포함
          };
          
          setUser(userDataWithLocation);
          setAccessToken(accessToken);
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('user', JSON.stringify(userDataWithLocation));
          
          // Flutter에게 토큰 정보 업데이트 전달
          flutterBridge.handleRefreshTokenFromFlutter(refreshToken, accessToken, userDataWithLocation);
          
          setIsAutoLoginInProgress(false);
          devLog('✅ Flutter 토큰으로 직접 자동 로그인 완료');
          return;
        }
        
        devLog('🔄 리프레시 토큰으로 자동 로그인 시도 시작');
        
        try {
          const response = await refreshTokenApi({ refreshToken });
          
          
          if (response.data.statusCode === 200) {
            // 로그인과 동일하게 헤더에서 토큰 추출
            const newAccessToken = response.headers?.get('access-token') || 
                                  response.headers?.get('access_token') || 
                                  response.headers?.get('authorization')?.replace('Bearer ', '') || '';
            
            const newRefreshToken = response.headers?.get('refresh-token') || 
                                   response.headers?.get('refresh_token') || '';
            
            devLog('✅ 자동 로그인 성공:', {
              newAccessTokenLength: newAccessToken.length,
              newRefreshTokenLength: newRefreshToken.length
            });
            
            if (newAccessToken) {
              // 기존 사용자 정보 또는 Flutter에서 전달받은 사용자 정보 확인
              const existingUser = localStorage.getItem('user');
              let userForContext = null;
              
              if (existingUser) {
                try {
                  userForContext = JSON.parse(existingUser);
                  devLog('✅ 기존 localStorage 사용자 정보 사용');
                } catch (error) {
                  devLog('⚠️ 잘못된 사용자 정보 제거:', error);
                  localStorage.removeItem('user');
                }
              }
              
              // 기존 사용자 정보가 없으면 Flutter에서 전달받은 userInfo 사용
              if (!userForContext && userInfo) {
                userForContext = userInfo;
                devLog('✅ Flutter에서 전달받은 사용자 정보 사용:', {
                  name: userInfo.name,
                  email: userInfo.email
                });
              }
              
              if (userForContext) {
                setUser(userForContext);
                setAccessToken(newAccessToken);
                localStorage.setItem('accessToken', newAccessToken);
                localStorage.setItem('user', JSON.stringify(userForContext));
                
                // 새로운 refresh token이 있다면 Flutter에게 전달
                if (newRefreshToken) {
                  flutterBridge.handleRefreshTokenFromFlutter(newRefreshToken, newAccessToken, userForContext);
                }
                
                devLog('🎉 자동 로그인 완료 - 사용자 정보 저장됨');
              } else {
                devLog('⚠️ 사용 가능한 사용자 정보가 없어 자동 로그인 중단');
              }
            } else {
              devLog('❌ 새로운 액세스 토큰이 없음');
            }
          } else {
            devLog('❌ 자동 로그인 실패 - 서버 응답 에러:', response.data.statusCode);
          }
        } catch (error) {
          devLog('❌ 자동 로그인 중 오류 발생:', error);
        } finally {
          setIsAutoLoginInProgress(false); // 자동 로그인 완료
        }
      }
    };

    window.addEventListener('flutter-auto-login', handleFlutterAutoLogin);
    
    // 초기화 완료 표시
    setIsInitialized(true);
    
    return () => {
      window.removeEventListener('flutter-auto-login', handleFlutterAutoLogin);
      unsubscribe(); // 구독 해제
      unsubscribeSession();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (serverResponseData: any, token: string) => {
    
    // 서버 응답에서 needMoreInfo만 사용 (isNew는 더 이상 사용하지 않음)
    const needMoreInfo = serverResponseData.needMoreInfo || false;
    
    
    // Context에는 필요한 정보만 저장 (민감한 정보 제외)
    const userDataForContext: User = {
      userId: serverResponseData.index?.toString() || serverResponseData.userId,
      index: serverResponseData.index, // index 값 추가로 저장
      email: serverResponseData.email,
      name: serverResponseData.name,
      providerType: serverResponseData.providerType,
      needMoreInfo,
      locationType: serverResponseData.locationType // locationType 추가
    };
    
    setUser(userDataForContext);
    setAccessToken(token);
    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(userDataForContext));
    
    // Refresh token은 localStorage에 저장하지 않음 (Flutter가 관리)
    
    // Flutter 환경 체크
    const isFlutter = flutterBridge.isFlutterWebView();
    
    // Flutter로 로그인 성공 알림 (항상 전송)
    const tokenData = {
      accessToken: token,
      refreshToken: serverResponseData.refreshToken, // 서버 응답에서 refresh token 추출
      userInfo: userDataForContext,
      timestamp: Date.now()
    };
    
    flutterBridge.sendLoginToken(tokenData);
    
    // localStorage에도 Flutter 전송 기록 저장
    try {
      localStorage.setItem('flutter_login_sent', JSON.stringify({
        tokenData,
        sentAt: new Date().toISOString(),
        isFlutter
      }));
    } catch (error) {
    }
    
  };

  const logout = () => {
    logoutLog('로그아웃 프로세스 시작');
    
    // Flutter 환경인지 먼저 확인
    const isFlutterWebView = flutterBridge.isFlutterWebView();
    logoutLog('Flutter 환경 감지', { isFlutterWebView });
    
    // 현재 로그인 상태 로깅
    logoutLog('로그아웃 전 상태', {
      hasUser: !!user,
      hasAccessToken: !!accessToken,
      userName: user?.name || user?.email,
      userId: user?.userId
    });

    try {
      // 1. Flutter로 로그아웃 알림 (항상 전송)
      flutterBridge.sendLogout();
      logoutLog('Flutter로 로그아웃 메시지 전송 완료');
      
      // 2. React 상태 초기화
      setUser(null);
      setAccessToken(null);
      logoutLog('React 상태 초기화 완료');
      
      // 3. localStorage 완전 정리
      try {
        // 인증 관련 데이터 제거
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        
        // Flutter 관련 백업 데이터도 정리
        localStorage.removeItem('flutter_token_data');
        localStorage.removeItem('flutter_login_sent');
        localStorage.removeItem('flutter_last_message');
        localStorage.removeItem('flutter_last_error');
        
        logoutLog('localStorage 정리 완료');
      } catch (storageError) {
        logoutLog('localStorage 정리 실패', storageError);
      }
      
      // 4. sessionStorage 디버그 데이터도 정리 (개발 모드)
      if (process.env.NODE_ENV === 'development') {
        try {
          sessionStorage.removeItem('flutter_refresh_token_debug');
          logoutLog('sessionStorage 디버그 데이터 정리 완료');
        } catch (sessionStorageError) {
          logoutLog('sessionStorage 정리 실패', sessionStorageError);
        }
      }
      
      // 5. Flutter 브릿지 상태 초기화
      if (flutterBridge && typeof flutterBridge.getReceivedRefreshToken === 'function') {
        // 브릿지의 내부 토큰 상태 초기화를 위한 로그아웃 이벤트 발생
        window.dispatchEvent(new CustomEvent('user-logout', {
          detail: { 
            timestamp: Date.now(),
            reason: 'manual_logout'
          }
        }));
        logoutLog('Flutter 브릿지 로그아웃 이벤트 발생');
      }

      logoutLog('로그아웃 프로세스 완료');
      
    } catch (error) {
      logoutLog('로그아웃 프로세스 중 오류 발생', error);
      
      // 오류가 발생해도 기본 로그아웃은 수행
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      
      logoutLog('오류 발생으로 기본 로그아웃만 수행함');
    }
  };

  // Flutter에서 받은 refresh token으로 자동 로그인 시도
  const attemptAutoLogin = async (refreshToken: string): Promise<boolean> => {
    try {
      
      const response = await refreshTokenApi({ refreshToken });
      
      if (response.data.statusCode === 200 && response.data.data) {
        const { accessToken, user: userData } = response.data.data;
        
        
        // 서버 응답 형태로 변환하여 login 함수 호출
        const serverResponseData = {
          ...userData,
          refreshToken: response.data.data.refreshToken || refreshToken
        };
        
        login(serverResponseData, accessToken);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  // 세션 만료 확인 핸들러
  const handleConfirmSessionExpired = () => {
    devLog('👋 [UserAuthContext] 세션 만료 확인 -> 로그아웃 및 로그인 페이지 이동');
    setIsSessionExpired(false);
    logout();
    
    // 현재 페이지가 로그인이 필요한 페이지인 경우에만 이동 (이미 로그인 페이지면 이동 X)
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
  };

  const isLoggedIn = !!user && !!accessToken;
  
  // 로그인 상태 변경 시 디버깅
  useEffect(() => {
  }, [user, accessToken, isLoggedIn]);

  return (
    <UserAuthContext.Provider value={{ 
      user, 
      accessToken, 
      isLoggedIn, 
      isInitialized, 
      isAutoLoginInProgress, 
      isTokenRefreshing, 
      login, 
      logout, 
      attemptAutoLogin, 
      setTokenRefreshing 
    }}>
      {children}
      <SessionExpiredModal 
        isOpen={isSessionExpired} 
        onConfirm={handleConfirmSessionExpired} 
      />
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = (): UserAuthContextType => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};
